import { GraphqlSubscriptionResponse } from "@/Common/api/types/GraphqlSubscriptionResponse";
import { useSubscriptions } from "@/Common/context/subscriptions/Subscriptions";
import { onSubmitPayrollToHR, onUpdatePayrollSubmitStatus } from "@/Washouts/api/PayrollClosing/subscriptions";
import { onSubmitPayrollToHRCallback, onUpdatePayrollSubmitStatusCallback } from "@/Washouts/helpers/PayrollClosing/subscriptionCallbacks";
import { IOnSubmitPayrollToHR, IOnUpdatePayrollSubmitStatus } from "@/Washouts/types/PayrollClosing/subscriptionCallbacks";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";

interface IUsePayrollClosingSubscriptionsArgs {
	locationId: string;
	commissionCycleId: string;
	payPeriodId: string;
	providerPayPeriodId: string;
	current: boolean;
}

export const usePayrollClosingSubscriptions = ({
	locationId,
	payPeriodId,
	providerPayPeriodId,
	current,
}: IUsePayrollClosingSubscriptionsArgs) => {
	// Subscriptions
	const { getSubscription, addSubscription } = useSubscriptions();
	const queryClient = useQueryClient();

	const identifier = useMemo(() => ["payrollClosing", payPeriodId], [payPeriodId]);

	const commissionCycleSubscription = getSubscription(identifier);

	commissionCycleSubscription?.subscriptions?.onSubmitPayrollToHR?.setCallback(
		(response: GraphqlSubscriptionResponse<"onSubmitPayrollToHR", IOnSubmitPayrollToHR>) =>
			onSubmitPayrollToHRCallback({ queryClient, response })
	);

	commissionCycleSubscription?.subscriptions?.onUpdatePayrollSubmitStatus?.setCallback(
		(response: GraphqlSubscriptionResponse<"onUpdatePayrollSubmitStatus", IOnUpdatePayrollSubmitStatus>) =>
			onUpdatePayrollSubmitStatusCallback({ queryClient, response })
	);

	useEffect(() => {
		if (!locationId || !payPeriodId || !current) {
			return;
		}

		addSubscription(identifier, [
			{
				context: "onSubmitPayrollToHR",
				query: onSubmitPayrollToHR,
				variables: {
					locationId,
					payPeriodId,
				},
			},
			{
				context: "onUpdatePayrollSubmitStatus",
				query: onUpdatePayrollSubmitStatus,
				variables: {
					locationId,
					payPeriodId,
				},
			},
		]);
	}, [locationId, payPeriodId, providerPayPeriodId, current]);
};
